import React, { useState, useEffect } from 'react';
import { XMarkIcon, MagnifyingGlassIcon, UserIcon, DocumentTextIcon, PhoneIcon, ClipboardDocumentListIcon, InformationCircleIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { Task, TaskFormData, TaskCategory, PRIORITY_OPTIONS, CompletionConditionConfig } from '../types/Task';
import { taskApi } from '../services/taskApi';
import { useBranch } from '../contexts/BranchContext';
import { 
  fromApiDate, 
  toApiDate, 
  validateDate, 
  validateDateRelationship
} from '../utils/dateUtils';
import api from '../services/api';

interface DocumentType {
  id: number;
  name: string;
}

interface FormOption {
  id: number;
  name: string;
}

const CALL_TYPES = [
  { value: 'live', label: 'Live Call' },
  { value: 'recorded', label: 'Recorded Call' },
  { value: 'scheduled', label: 'Scheduled Call' }
];

interface PreSelectedLead {
  id: number;
  name: string;
  email: string;
  phone: string;
}

interface TaskFormProps {
  task?: Task | null;
  onClose: () => void;
  onSave?: (task: Task) => void;
  onSuccess?: () => void;
  preSelectedLead?: PreSelectedLead;
}

const TaskForm: React.FC<TaskFormProps> = ({ task, onClose, onSave, onSuccess, preSelectedLead }) => {
  const { currentBranch, isViewingAllBranches, loading: branchLoading } = useBranch();
  const [formData, setFormData] = useState<TaskFormData>({
    name: '',
    categoryId: null,
    startDate: '',
    dueDate: '',
    reminderDate: '',
    priority: 'medium',
    description: '',
    leadIds: [],
    assigneeIds: [],
    supervisorIds: [],
    completionConditionType: null,
    completionConditionConfig: null
  });

  const [categories, setCategories] = useState<TaskCategory[]>([]);
  const [leads, setLeads] = useState<any[]>([]);
  const [allLeads, setAllLeads] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  
  // Completion condition states
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [forms, setForms] = useState<FormOption[]>([]);
  const [showConditionConfig, setShowConditionConfig] = useState(false);

  // Search states
  const [leadSearchTerm, setLeadSearchTerm] = useState('');
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('');
  const [supervisorSearchTerm, setSupervisorSearchTerm] = useState('');
  const [showLeadDropdown, setShowLeadDropdown] = useState(false);
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [showSupervisorDropdown, setShowSupervisorDropdown] = useState(false);
  const [reminderType, setReminderType] = useState<'manual' | 'quick'>('manual');

  const isEdit = !!task;

  useEffect(() => {
    if (!branchLoading) {
      fetchInitialData();
      
      // Handle pre-selected lead
      if (preSelectedLead) {
        console.log('🔍 TaskForm - Setting pre-selected lead:', preSelectedLead);
        setFormData(prev => ({
          ...prev,
          leadIds: [preSelectedLead.id]
        }));
        setAllLeads([preSelectedLead]);
      }
    }
  }, [branchLoading, preSelectedLead]);

  // Clear leads when branch changes to ensure branch-specific leads
  useEffect(() => {
    if (!branchLoading) {
      setLeads([]);
      setAllLeads([]);
      setLeadSearchTerm('');
      setShowLeadDropdown(false);
      
      // Clear selected leads from form data to prevent cross-branch assignments
      // BUT preserve preSelectedLead if it exists
      setFormData(prev => ({
        ...prev,
        leadIds: preSelectedLead ? [preSelectedLead.id] : []
      }));
      
      // If preSelectedLead exists, re-add it to allLeads
      if (preSelectedLead) {
        setAllLeads([preSelectedLead]);
        console.log('🔍 TaskForm - Preserving pre-selected lead after branch change:', preSelectedLead);
      }
    }
  }, [currentBranch, isViewingAllBranches, branchLoading, preSelectedLead]);

  // Handle task editing data
  useEffect(() => {
    if (task) {
      setFormData({
        name: task.name,
        categoryId: task.categoryId || null,
        startDate: fromApiDate({ date: task.startDate }) || '',
        dueDate: fromApiDate({ date: task.dueDate }) || '',
        reminderDate: fromApiDate({ date: task.reminderDate }) || '',
        priority: task.priority,
        description: task.description || '',
        leadIds: task.leads?.map(l => l.id) || [],
        assigneeIds: task.assignees?.map(a => a.id) || [],
        supervisorIds: task.supervisors?.map(s => s.id) || [],
        completionConditionType: task.completionConditionType || null,
        completionConditionConfig: task.completionConditionConfig || null
      });
      
      // Show condition config section if task has a completion condition
      if (task.completionConditionType) {
        setShowConditionConfig(true);
      }
      
      // Populate allLeads and allUsers with existing data
      if (task.leads) {
        const leadData = task.leads.map(lead => ({
          id: lead.id,
          name: lead.name || `${lead.first_name || ''} ${lead.last_name || ''}`.trim(),
          email: lead.email,
          phone: lead.phone
        }));
        setAllLeads(leadData);
        setLeads(leadData); // Also set leads for dropdown display
      }
      
      if (task.assignees || task.supervisors) {
        const existingUsers = [...(task.assignees || []), ...(task.supervisors || [])];
        const uniqueUsers = existingUsers.filter((user, index, self) => 
          index === self.findIndex(u => u.id === user.id)
        );
        const userData = uniqueUsers.map(user => ({
          id: user.id,
          firstName: user.first_name || user.firstName,
          lastName: user.last_name || user.lastName,
          email: user.email
        }));
        setAllUsers(userData);
        setUsers(userData); // Also set users for dropdown display
      }

      // Set reminder type based on existing data
      if (task.reminderDate) {
        setReminderType('manual');
      }
    }
  }, [task, preSelectedLead]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [categoriesRes, usersRes] = await Promise.all([
        taskApi.getTaskCategories(),
        taskApi.getUsers()
      ]);

      if (categoriesRes.success) {
        setCategories(categoriesRes.data);
      }
      if (usersRes.success) {
        setUsers(usersRes.data);
        setAllUsers(usersRes.data);
      }

      // Fetch document types and forms for completion conditions
      try {
        const [docTypesRes, formsRes] = await Promise.all([
          api.get('/documents/types?limit=100'),
          api.get('/forms?limit=100')
        ]);
        
        if (docTypesRes.data?.success && docTypesRes.data?.data) {
          setDocumentTypes(docTypesRes.data.data);
        }
        if (formsRes.data?.success && formsRes.data?.data) {
          setForms(formsRes.data.data.map((f: any) => ({ id: f.id, name: f.name })));
        }
      } catch (err) {
        console.error('Error fetching completion condition options:', err);
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof TaskFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleQuickReminderChange = (reminderOption: string) => {
    if (!formData.startDate) {
      alert('Please select a start date first');
      return;
    }

    const startDate = new Date(formData.startDate);
    let reminderDate = new Date(startDate);

    switch (reminderOption) {
      case '1-hour-before':
        reminderDate.setHours(startDate.getHours() - 1);
        break;
      case '2-hours-before':
        reminderDate.setHours(startDate.getHours() - 2);
        break;
      case '1-day-before':
        reminderDate.setDate(startDate.getDate() - 1);
        break;
      case '2-days-before':
        reminderDate.setDate(startDate.getDate() - 2);
        break;
      case '1-week-before':
        reminderDate.setDate(startDate.getDate() - 7);
        break;
      case '2-weeks-before':
        reminderDate.setDate(startDate.getDate() - 14);
        break;
      case '1-month-before':
        reminderDate.setMonth(startDate.getMonth() - 1);
        break;
      default:
        return;
    }

    // Format the date for the datetime-local input
    const year = reminderDate.getFullYear();
    const month = String(reminderDate.getMonth() + 1).padStart(2, '0');
    const day = String(reminderDate.getDate()).padStart(2, '0');
    const hours = String(reminderDate.getHours()).padStart(2, '0');
    const minutes = String(reminderDate.getMinutes()).padStart(2, '0');
    
    const formattedDate = `${year}-${month}-${day}T${hours}:${minutes}`;
    setFormData(prev => ({ ...prev, reminderDate: formattedDate }));
  };

  const handleLeadSearch = async (searchTerm: string) => {
    setLeadSearchTerm(searchTerm);
    if (searchTerm.trim().length > 0) {
      try {
        const response = await taskApi.searchLeads(searchTerm);
        if (response.success) {
          setLeads(response.data);
          setShowLeadDropdown(true);
        }
      } catch (error) {
        console.error('Error searching leads:', error);
      }
    } else {
      setLeads([]);
      setShowLeadDropdown(false);
    }
  };

  const handleAssigneeSearch = (searchTerm: string) => {
    setAssigneeSearchTerm(searchTerm);
    if (searchTerm.trim().length > 0) {
      const filtered = allUsers.filter(user => {
        const firstName = user.firstName || user.first_name || '';
        const lastName = user.lastName || user.last_name || '';
        return firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
               lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
               user.email.toLowerCase().includes(searchTerm.toLowerCase());
      });
      setUsers(filtered);
      setShowAssigneeDropdown(true);
    } else {
      // Show all users when search is empty
      setUsers(allUsers);
      setShowAssigneeDropdown(true);
    }
  };

  const handleSupervisorSearch = (searchTerm: string) => {
    setSupervisorSearchTerm(searchTerm);
    if (searchTerm.trim().length > 0) {
      const filtered = allUsers.filter(user => {
        const firstName = user.firstName || user.first_name || '';
        const lastName = user.lastName || user.last_name || '';
        return firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
               lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
               user.email.toLowerCase().includes(searchTerm.toLowerCase());
      });
      setUsers(filtered);
      setShowSupervisorDropdown(true);
    } else {
      // Show all users when search is empty
      setUsers(allUsers);
      setShowSupervisorDropdown(true);
    }
  };

  const handleLeadSelect = (lead: any) => {
    if (!formData.leadIds.includes(lead.id)) {
      setFormData(prev => ({
        ...prev,
        leadIds: [...prev.leadIds, lead.id]
      }));
      // Add to allLeads if not already there
      setAllLeads(prev => {
        if (!prev.find(l => l.id === lead.id)) {
          return [...prev, lead];
        }
        return prev;
      });
    }
    setLeadSearchTerm('');
    setShowLeadDropdown(false);
  };

  const handleAssigneeSelect = (user: any) => {
    if (!formData.assigneeIds.includes(user.id)) {
      setFormData(prev => ({
        ...prev,
        assigneeIds: [...prev.assigneeIds, user.id]
      }));
      // Add to allUsers if not already there
      setAllUsers(prev => {
        if (!prev.find(u => u.id === user.id)) {
          return [...prev, user];
        }
        return prev;
      });
    }
    setAssigneeSearchTerm('');
    setShowAssigneeDropdown(false);
  };

  const handleSupervisorSelect = (user: any) => {
    if (!formData.supervisorIds.includes(user.id)) {
      setFormData(prev => ({
        ...prev,
        supervisorIds: [...prev.supervisorIds, user.id]
      }));
      // Add to allUsers if not already there
      setAllUsers(prev => {
        if (!prev.find(u => u.id === user.id)) {
          return [...prev, user];
        }
        return prev;
      });
    }
    setSupervisorSearchTerm('');
    setShowSupervisorDropdown(false);
  };

  const handleRemoveLead = (leadId: number) => {
    setFormData(prev => ({
      ...prev,
      leadIds: prev.leadIds.filter(id => id !== leadId)
    }));
    // Remove from allLeads as well
    setAllLeads(prev => prev.filter(lead => lead.id !== leadId));
  };

  const handleRemoveAssignee = (userId: number) => {
    setFormData(prev => ({
      ...prev,
      assigneeIds: prev.assigneeIds.filter(id => id !== userId)
    }));
    // Note: We don't remove from allUsers as the user might be selected as supervisor too
  };

  const handleRemoveSupervisor = (userId: number) => {
    setFormData(prev => ({
      ...prev,
      supervisorIds: prev.supervisorIds.filter(id => id !== userId)
    }));
    // Note: We don't remove from allUsers as the user might be selected as assignee too
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Task name is required';
    }

    // Date validations using utility functions
    const startDateValidation = validateDate(formData.startDate);
    if (!startDateValidation.isValid) {
      newErrors.startDate = startDateValidation.error || 'Invalid start date format';
    }

    const dueDateValidation = validateDate(formData.dueDate);
    if (!dueDateValidation.isValid) {
      newErrors.dueDate = dueDateValidation.error || 'Invalid due date format';
    }

    const reminderDateValidation = validateDate(formData.reminderDate);
    if (!reminderDateValidation.isValid) {
      newErrors.reminderDate = reminderDateValidation.error || 'Invalid reminder date format';
    }

    // Date relationship validations
    const dueDateRelationship = validateDateRelationship(
      formData.startDate, 
      formData.dueDate, 
      'Due date'
    );
    if (!dueDateRelationship.isValid) {
      newErrors.dueDate = dueDateRelationship.error || 'Due date must be after start date';
    }

    // A reminder is a heads-up BEFORE the task is due, so it may legitimately fall
    // before the start date (e.g. the "1 Day / 1 Week / 1 Month Before" quick
    // options). Only guard that it is not set AFTER the due date.
    if (formData.dueDate) {
      const reminderVsDue = validateDateRelationship(
        formData.reminderDate,
        formData.dueDate,
        'Reminder date'
      );
      if (!reminderVsDue.isValid) {
        newErrors.reminderDate = 'Reminder date should be on or before the due date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      
      // Safety check: If preSelectedLead exists, ensure its ID is in leadIds
      let finalLeadIds = formData.leadIds;
      if (preSelectedLead && (!finalLeadIds || !finalLeadIds.includes(preSelectedLead.id))) {
        console.log('⚠️ TaskForm - Pre-selected lead ID missing from leadIds, adding it:', preSelectedLead.id);
        finalLeadIds = preSelectedLead.id 
          ? [...(finalLeadIds || []).filter(id => id !== preSelectedLead.id), preSelectedLead.id]
          : finalLeadIds;
      }
      
      // Process dates for API submission
      const submitData = {
        ...formData,
        leadIds: finalLeadIds,
        startDate: toApiDate({ date: formData.startDate }) || '',
        dueDate: toApiDate({ date: formData.dueDate }) || '',
        reminderDate: toApiDate({ date: formData.reminderDate }) || ''
      };
      
      console.log('🔍 TaskForm - Submitting task with data:', submitData);
      console.log('🔍 TaskForm - leadIds in submit:', submitData.leadIds);
      console.log('🔍 TaskForm - preSelectedLead:', preSelectedLead);
      
      let response;
      
      if (isEdit) {
        response = await taskApi.updateTask(task!.id, submitData);
      } else {
        response = await taskApi.createTask(submitData);
      }

      if (response.success) {
        if (onSave) {
          onSave(response.data);
        }
        if (onSuccess) {
          onSuccess();
        }
      } else {
        alert((response as any).message || 'Failed to save task');
      }
    } catch (error: any) {
      console.error('Error saving task:', error);
      // Surface the actual reason from the server (e.g. validation / permission)
      const message = error?.response?.data?.message
        || (error?.response?.data?.errors && error.response.data.errors.join(', '))
        || error?.message
        || 'Failed to save task';
      alert(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-center mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEdit ? 'Edit Task' : 'Create New Task'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Task Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Task Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter task name"
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          {/* Category and Priority */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Task Category
              </label>
              <select
                value={formData.categoryId || ''}
                onChange={(e) => handleInputChange('categoryId', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Category</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={formData.priority}
                onChange={(e) => handleInputChange('priority', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {PRIORITY_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.startDate ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.startDate && <p className="mt-1 text-sm text-red-600">{errors.startDate}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Date
              </label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={(e) => handleInputChange('dueDate', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.dueDate ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.dueDate && <p className="mt-1 text-sm text-red-600">{errors.dueDate}</p>}
            </div>
          </div>

          {/* Reminder */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reminder
            </label>
            <div className="flex items-center gap-3 mb-3">
              <button
                type="button"
                onClick={() => setReminderType('quick')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-all ${
                  reminderType === 'quick'
                    ? 'bg-blue-50 text-blue-700 border-blue-300'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Quick Selection
              </button>
              <button
                type="button"
                onClick={() => setReminderType('manual')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-all ${
                  reminderType === 'manual'
                    ? 'bg-blue-50 text-blue-700 border-blue-300'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Manual Date/Time
              </button>
              {formData.reminderDate && (
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, reminderDate: '' }))}
                  className="px-3 py-1.5 text-xs font-medium rounded-md border border-red-200 text-red-600 hover:bg-red-50 transition-all ml-auto"
                >
                  Clear
                </button>
              )}
            </div>

            {reminderType === 'quick' && (
              <div className="flex flex-wrap gap-2">
                {[
                  { key: '1-hour-before', label: '1 Hour Before' },
                  { key: '2-hours-before', label: '2 Hours Before' },
                  { key: '1-day-before', label: '1 Day Before' },
                  { key: '2-days-before', label: '2 Days Before' },
                  { key: '1-week-before', label: '1 Week Before' },
                  { key: '2-weeks-before', label: '2 Weeks Before' },
                  { key: '1-month-before', label: '1 Month Before' },
                ].map(option => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => handleQuickReminderChange(option.key)}
                    className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}

            {reminderType === 'manual' && (
              <div className="max-w-sm">
                <input
                  type="datetime-local"
                  value={formData.reminderDate}
                  onChange={(e) => handleInputChange('reminderDate', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.reminderDate ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.reminderDate && <p className="mt-1 text-sm text-red-600">{errors.reminderDate}</p>}
              </div>
            )}

            {formData.reminderDate && (
              <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-md">
                <span className="text-xs text-blue-800">
                  Reminder set for <strong>{new Date(formData.reminderDate).toLocaleString()}</strong>
                </span>
              </div>
            )}
          </div>

          {/* Lead Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Leads
              {preSelectedLead && (
                <span className="text-xs text-gray-500 ml-2">(Pre-selected from Lead Details)</span>
              )}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={leadSearchTerm}
                onChange={(e) => handleLeadSearch(e.target.value)}
                onFocus={() => {
                  setLeads(allLeads);
                  setShowLeadDropdown(true);
                }}
                placeholder={preSelectedLead ? "Lead is pre-selected and locked" : "Search leads by name, email, or phone..."}
                disabled={!!preSelectedLead}
                className={`w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  preSelectedLead ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
              />
              {showLeadDropdown && (leads.length > 0 || allLeads.length > 0) && !preSelectedLead && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                  {(leads.length > 0 ? leads : allLeads).map(lead => (
                    <div
                      key={lead.id}
                      onClick={() => handleLeadSelect(lead)}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    >
                      <div className="font-medium">{lead.name}</div>
                      <div className="text-sm text-gray-500">{lead.email}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Selected Leads */}
            {formData.leadIds.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {formData.leadIds.map(leadId => {
                  const lead = allLeads.find(l => l.id === leadId);
                  return lead ? (
                    <span
                      key={leadId}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                        preSelectedLead ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {lead.name}
                      {!preSelectedLead && (
                        <button
                          type="button"
                          onClick={() => handleRemoveLead(leadId)}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      )}
                    </span>
                  ) : null;
                })}
              </div>
            )}
          </div>

          {/* Assignee Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assignees
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <UserIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={assigneeSearchTerm}
                onChange={(e) => handleAssigneeSearch(e.target.value)}
                onFocus={() => {
                  setUsers(allUsers);
                  setShowAssigneeDropdown(true);
                }}
                placeholder="Search users by name or email..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {showAssigneeDropdown && users.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                  {users.map(user => (
                    <div
                      key={user.id}
                      onClick={() => handleAssigneeSelect(user)}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    >
                      <div className="font-medium">{user.firstName || user.first_name} {user.lastName || user.last_name}</div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Selected Assignees */}
            {formData.assigneeIds.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {formData.assigneeIds.map(userId => {
                  const user = allUsers.find(u => u.id === userId);
                  return user ? (
                    <span
                      key={userId}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800"
                    >
                      {user.firstName || user.first_name} {user.lastName || user.last_name}
                      <button
                        type="button"
                        onClick={() => handleRemoveAssignee(userId)}
                        className="ml-2 text-green-600 hover:text-green-800"
                      >
                        ×
                      </button>
                    </span>
                  ) : null;
                })}
              </div>
            )}
          </div>

          {/* Supervisor Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Supervisors
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <UserIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={supervisorSearchTerm}
                onChange={(e) => handleSupervisorSearch(e.target.value)}
                onFocus={() => {
                  setUsers(allUsers);
                  setShowSupervisorDropdown(true);
                }}
                placeholder="Search users by name or email..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {showSupervisorDropdown && users.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                  {users.map(user => (
                    <div
                      key={user.id}
                      onClick={() => handleSupervisorSelect(user)}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    >
                      <div className="font-medium">{user.firstName || user.first_name} {user.lastName || user.last_name}</div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Selected Supervisors */}
            {formData.supervisorIds.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {formData.supervisorIds.map(userId => {
                  const user = allUsers.find(u => u.id === userId);
                  return user ? (
                    <span
                      key={userId}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800"
                    >
                      {user.firstName || user.first_name} {user.lastName || user.last_name}
                      <button
                        type="button"
                        onClick={() => handleRemoveSupervisor(userId)}
                        className="ml-2 text-purple-600 hover:text-purple-800"
                      >
                        ×
                      </button>
                    </span>
                  ) : null;
                })}
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Task Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter task description"
            />
          </div>

          {/* Completion Condition Section */}
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <InformationCircleIcon className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-gray-700">Completion Requirement (Optional)</span>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showConditionConfig}
                  onChange={(e) => {
                    setShowConditionConfig(e.target.checked);
                    if (!e.target.checked) {
                      handleInputChange('completionConditionType', null);
                      handleInputChange('completionConditionConfig', null);
                    }
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-600">Enable</span>
              </label>
            </div>

            {showConditionConfig && (
              <div className="space-y-4">
                <p className="text-xs text-gray-500">
                  Add a completion requirement - this task can only be completed when the specified action is performed for the attached lead(s).
                </p>

                {/* Condition Type Selector */}
                <div className="grid grid-cols-4 gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      handleInputChange('completionConditionType', 'document_upload');
                      handleInputChange('completionConditionConfig', { documentTypeIds: [], requireAll: true });
                    }}
                    className={`p-3 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${
                      formData.completionConditionType === 'document_upload'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <DocumentTextIcon className={`w-6 h-6 ${formData.completionConditionType === 'document_upload' ? 'text-blue-600' : 'text-gray-400'}`} />
                    <span className={`text-xs font-medium ${formData.completionConditionType === 'document_upload' ? 'text-blue-700' : 'text-gray-600'}`}>
                      Document
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      handleInputChange('completionConditionType', 'call_completed');
                      handleInputChange('completionConditionConfig', { callTypes: [] });
                    }}
                    className={`p-3 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${
                      formData.completionConditionType === 'call_completed'
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <PhoneIcon className={`w-6 h-6 ${formData.completionConditionType === 'call_completed' ? 'text-green-600' : 'text-gray-400'}`} />
                    <span className={`text-xs font-medium ${formData.completionConditionType === 'call_completed' ? 'text-green-700' : 'text-gray-600'}`}>
                      Call
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      handleInputChange('completionConditionType', 'form_submitted');
                      handleInputChange('completionConditionConfig', { formId: null });
                    }}
                    className={`p-3 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${
                      formData.completionConditionType === 'form_submitted'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <ClipboardDocumentListIcon className={`w-6 h-6 ${formData.completionConditionType === 'form_submitted' ? 'text-purple-600' : 'text-gray-400'}`} />
                    <span className={`text-xs font-medium ${formData.completionConditionType === 'form_submitted' ? 'text-purple-700' : 'text-gray-600'}`}>
                      Form
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      handleInputChange('completionConditionType', 'email_sent');
                      handleInputChange('completionConditionConfig', {});
                    }}
                    className={`p-3 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${
                      formData.completionConditionType === 'email_sent'
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <EnvelopeIcon className={`w-6 h-6 ${formData.completionConditionType === 'email_sent' ? 'text-orange-600' : 'text-gray-400'}`} />
                    <span className={`text-xs font-medium ${formData.completionConditionType === 'email_sent' ? 'text-orange-700' : 'text-gray-600'}`}>
                      Email
                    </span>
                  </button>
                </div>

                {/* Document Upload Config */}
                {formData.completionConditionType === 'document_upload' && (
                  <div className="space-y-3 p-3 bg-white border border-blue-200 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700">
                      Select Required Documents
                    </label>
                    <div className="max-h-40 overflow-y-auto space-y-2 border border-gray-200 rounded-md p-2">
                      {documentTypes.length === 0 ? (
                        <p className="text-xs text-gray-500 italic">No document types found</p>
                      ) : (
                        documentTypes.map((dt) => (
                          <label key={dt.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                            <input
                              type="checkbox"
                              checked={formData.completionConditionConfig?.documentTypeIds?.includes(dt.id) || false}
                              onChange={(e) => {
                                const currentIds = formData.completionConditionConfig?.documentTypeIds || [];
                                const newIds = e.target.checked
                                  ? [...currentIds, dt.id]
                                  : currentIds.filter((id: number) => id !== dt.id);
                                handleInputChange('completionConditionConfig', {
                                  ...formData.completionConditionConfig,
                                  documentTypeIds: newIds
                                });
                              }}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="text-sm text-gray-700">{dt.name}</span>
                          </label>
                        ))
                      )}
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.completionConditionConfig?.requireAll !== false}
                        onChange={(e) => {
                          handleInputChange('completionConditionConfig', {
                            ...formData.completionConditionConfig,
                            requireAll: e.target.checked
                          });
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-600">Require ALL selected documents</span>
                    </label>
                  </div>
                )}

                {/* Call Completed Config */}
                {formData.completionConditionType === 'call_completed' && (
                  <div className="space-y-3 p-3 bg-white border border-green-200 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700">
                      Select Acceptable Call Types
                    </label>
                    <div className="space-y-2">
                      {CALL_TYPES.map((ct) => (
                        <label key={ct.value} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                          <input
                            type="checkbox"
                            checked={formData.completionConditionConfig?.callTypes?.includes(ct.value) || false}
                            onChange={(e) => {
                              const currentTypes = formData.completionConditionConfig?.callTypes || [];
                              const newTypes = e.target.checked
                                ? [...currentTypes, ct.value]
                                : currentTypes.filter((t: string) => t !== ct.value);
                              handleInputChange('completionConditionConfig', {
                                ...formData.completionConditionConfig,
                                callTypes: newTypes
                              });
                            }}
                            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                          />
                          <span className="text-sm text-gray-700">{ct.label}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500">
                      Leave all unchecked to accept any call type.
                    </p>
                  </div>
                )}

                {/* Form Submitted Config */}
                {formData.completionConditionType === 'form_submitted' && (
                  <div className="space-y-3 p-3 bg-white border border-purple-200 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700">
                      Select Required Form
                    </label>
                    <select
                      value={formData.completionConditionConfig?.formId || ''}
                      onChange={(e) => {
                        handleInputChange('completionConditionConfig', {
                          ...formData.completionConditionConfig,
                          formId: e.target.value ? parseInt(e.target.value) : null
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Any form (select for specific form)</option>
                      {forms.map((f) => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500">
                      The task will auto-complete when this form is attached to the lead.
                    </p>
                  </div>
                )}

                {/* Email Sent Config */}
                {formData.completionConditionType === 'email_sent' && (
                  <div className="space-y-3 p-3 bg-white border border-orange-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <EnvelopeIcon className="w-5 h-5 text-orange-500 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Email Requirement</p>
                        <p className="text-xs text-gray-500 mt-1">
                          The task will auto-complete when any email is sent to the attached lead(s).
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {saving ? 'Saving...' : (isEdit ? 'Update Task' : 'Create Task')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaskForm;
